import { useEffect, useRef, useState } from 'react'
import type { CostShiftFilterState } from '@/components/Dashboard/costShiftGridMockData'
import type { PoLevelDetailFilterState } from '@/components/Dashboard/poLevelDetailViewMockData'
import { useParams } from 'react-router-dom'
import {
  AlertCircle,
  BarChart3,
  ChevronLeft,
  Download,
  Loader2,
  RotateCcw,
} from 'lucide-react'
import { AxiosError } from 'axios'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PowerBiReportEmbed } from '@/components/common/PowerBiReportEmbed'
import { FinancialsReportMockup } from '@/components/Dashboard/FinancialsReportMockup'
import { ParetoCurveMockup } from '@/components/Dashboard/ParetoCurveMockup'
import { AttributesReportMockup } from '@/components/Dashboard/AttributesReportMockup'
import { EventProductFlowMockup } from '@/components/Dashboard/EventProductFlowMockup'
import { CostShiftGridMockup } from '@/components/Dashboard/CostShiftGridMockup'
import { POLevelDetailViewMockup } from '@/components/Dashboard/POLevelDetailViewMockup'
import { StoreCubeCartonUpdateMockup } from '@/components/Dashboard/StoreCubeCartonUpdateMockup'
import { InboundFlowReportMockup } from '@/components/Dashboard/InboundFlowReportMockup'
import { POGOnTimeProjectionMockup } from '@/components/Dashboard/POGOnTimeProjectionMockup'
import { SeasonEventDispositionMockup } from '@/components/Dashboard/SeasonEventDispositionMockup'
import { type PowerBiDashboardType } from '@/api/powerbi'
import { POWERBI_REPORTS } from '@/config/powerbiReports'
import { usePowerBiDashboard } from '@/hooks/usePowerBiDashboard'
import { useDashboardExport } from '@/hooks/useDashboardExport'
import { toast } from '@/stores/useToastStore'

interface DrillContext {
  fromReportId: string
  filters: Partial<PoLevelDetailFilterState>
}

function getApiErrorMessage(error: unknown): string {
  if (error instanceof AxiosError) {
    const detail = error.response?.data?.detail
    if (typeof detail === 'string' && detail.trim()) return detail
  }
  if (error instanceof Error) return error.message
  return 'Failed to load Power BI dashboard'
}

export default function Dashboard() {
  const { workspace } = useParams<{ workspace: string }>()
  const selectedDashboard: PowerBiDashboardType =
    workspace?.toUpperCase() === 'IFP' ? 'IFP' : 'CAT'

  const reports = POWERBI_REPORTS[selectedDashboard]
  const [selectedReportId, setSelectedReportId] = useState<string | null>(
    reports[0]?.reportId ?? null
  )

  const [drillContext, setDrillContext] = useState<DrillContext | null>(null)
  const [mockupResetKey, setMockupResetKey] = useState(0)

  const contentRef = useRef<HTMLDivElement>(null)
  const lastToastMessageRef = useRef<string | null>(null)

  const selectedReport = reports.find(r => r.reportId === selectedReportId)
  const isFinancialsMockup =
    selectedDashboard === 'CAT' && selectedReport?.label === 'Financials'
  const isParetoMockup =
    selectedDashboard === 'CAT' && selectedReport?.label === 'Pareto Curve'
  const isAttributesMockup =
    selectedDashboard === 'CAT' && selectedReport?.label === 'Attributes'
  const isEventProductFlowMockup =
    selectedDashboard === 'IFP' &&
    selectedReport?.label === 'Event Product Flow'
  const isCostShiftGridMockup =
    selectedDashboard === 'IFP' && selectedReport?.label === 'Cost Shift Grid'
  const isPoLevelDetailMockup =
    selectedDashboard === 'IFP' &&
    selectedReport?.label === 'PO Level Detail View'
  const isStoreCubeCartonMockup =
    selectedDashboard === 'IFP' &&
    selectedReport?.label === 'Store Cube Carton Update'
  const isInboundFlowReportMockup =
    selectedDashboard === 'IFP' &&
    selectedReport?.label === 'Inbound Flow Report'
  const isPogOnTimeProjectionMockup =
    selectedDashboard === 'IFP' &&
    selectedReport?.label === 'POG On-Time Projection'
  const isSeasonEventDispositionMockup =
    selectedDashboard === 'IFP' &&
    selectedReport?.label === 'Season Event Disposition'

  const isMockup =
    isFinancialsMockup ||
    isParetoMockup ||
    isAttributesMockup ||
    isEventProductFlowMockup ||
    isCostShiftGridMockup ||
    isPoLevelDetailMockup ||
    isStoreCubeCartonMockup ||
    isInboundFlowReportMockup ||
    isPogOnTimeProjectionMockup ||
    isSeasonEventDispositionMockup

  const {
    data: embedConfig,
    error,
    isError,
    isFetching,
    isLoading,
    refetch,
  } = usePowerBiDashboard(selectedReportId, selectedDashboard, {
    enabled:
      !isFinancialsMockup &&
      !isParetoMockup &&
      !isAttributesMockup &&
      !isEventProductFlowMockup &&
      !isCostShiftGridMockup &&
      !isPoLevelDetailMockup &&
      !isStoreCubeCartonMockup &&
      !isInboundFlowReportMockup &&
      !isPogOnTimeProjectionMockup &&
      !isSeasonEventDispositionMockup,
  })

  useEffect(() => {
    if (!isError) return

    const message = getApiErrorMessage(error)

    if (lastToastMessageRef.current !== message) {
      lastToastMessageRef.current = message
      toast.error(message)
    }
  }, [error, isError])

  useEffect(() => {
    const newReports = POWERBI_REPORTS[selectedDashboard]
    setSelectedReportId(newReports[0]?.reportId ?? null)
    setDrillContext(null)
    lastToastMessageRef.current = null
  }, [selectedDashboard])

  const { exportAsPdf, exportAsPpt, isExporting } = useDashboardExport(
    contentRef,
    selectedReport?.label ?? selectedDashboard
  )

  const handleCostShiftDrill = (
    from: string,
    to: string,
    csgFilters: CostShiftFilterState
  ) => {
    const id = reports.find(r => r.label === 'PO Level Detail View')?.reportId
    if (!id) return
    setDrillContext({
      fromReportId: selectedReportId!,
      filters: {
        origArrWk: from,
        estimatedArrWk: to,
        event: csgFilters.event,
        dc: csgFilters.dc,
        season: csgFilters.season,
        snapshotDate: csgFilters.snapshotDate,
        divisionId: csgFilters.divisionId,
        departmentId: csgFilters.departmentId,
        classId: csgFilters.classId,
        subclassId: csgFilters.subclassId,
      },
    })
    setSelectedReportId(id)
    lastToastMessageRef.current = null
  }

  const handleDrillBack = () => {
    if (!drillContext) return
    setSelectedReportId(drillContext.fromReportId)
    setDrillContext(null)
    lastToastMessageRef.current = null
  }

  const handleRefresh = () => {
    lastToastMessageRef.current = null
    if (!selectedReportId) return
    void refetch()
  }

  const handleMockupReset = () => {
    setMockupResetKey(key => key + 1)
  }

  const hasReports = reports.length > 0

  const showErrorState =
    hasReports &&
    selectedReportId != null &&
    !isFinancialsMockup &&
    !isParetoMockup &&
    !isAttributesMockup &&
    !isEventProductFlowMockup &&
    !isCostShiftGridMockup &&
    !isPoLevelDetailMockup &&
    !isStoreCubeCartonMockup &&
    !isInboundFlowReportMockup &&
    !isPogOnTimeProjectionMockup &&
    !isSeasonEventDispositionMockup &&
    !isLoading &&
    !isFetching &&
    (isError || !embedConfig)

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          {hasReports ? (
            <Select
              value={selectedReportId ?? undefined}
              onValueChange={value => {
                setSelectedReportId(value)
                setDrillContext(null)
                lastToastMessageRef.current = null
              }}
            >
              <SelectTrigger className="w-auto gap-2 border-none bg-transparent p-0 text-2xl font-semibold text-foreground shadow-none focus:ring-0 cursor-pointer">
                <SelectValue placeholder="Select a report" />
              </SelectTrigger>
              <SelectContent>
                {reports.map(report => (
                  <SelectItem
                    key={report.reportId}
                    value={report.reportId}
                    className="cursor-pointer"
                  >
                    {report.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <h1 className="text-2xl font-semibold text-foreground">
              {selectedDashboard} Dashboard
            </h1>
          )}
          {drillContext && (
            <button
              onClick={handleDrillBack}
              className="flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <ChevronLeft className="h-3 w-3" />
              Cost Shift Grid
            </button>
          )}
        </div>
      </div>

      <Card className="overflow-hidden">
        <CardContent className="p-0">
          {isMockup && (
            <div className="flex items-center justify-end gap-1 px-2 py-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 cursor-pointer"
                    disabled={
                      isExporting || !selectedReportId || showErrorState
                    }
                    aria-label="Export dashboard"
                  >
                    {isExporting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => void exportAsPdf()}
                    className="cursor-pointer"
                  >
                    Export as PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => void exportAsPpt()}
                    className="cursor-pointer"
                  >
                    Export as PowerPoint
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 cursor-pointer"
                onClick={handleMockupReset}
                aria-label="Reset dashboard"
                title="Reset dashboard"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          )}

          <div ref={contentRef} key={mockupResetKey}>
            {!hasReports ? (
              <div className="flex min-h-180 flex-col items-center justify-center gap-4 px-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <BarChart3 className="h-6 w-6" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold text-foreground">
                    No reports available
                  </h2>
                  <p className="max-w-xl text-sm text-muted-foreground">
                    There are no Power BI reports configured for the{' '}
                    {selectedDashboard} dashboard yet.
                  </p>
                </div>
              </div>
            ) : showErrorState ? (
              <div className="flex min-h-180 flex-col items-center justify-center gap-4 px-6 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold text-foreground">
                    Unable to load dashboard
                  </h2>
                  <p className="max-w-xl text-sm text-muted-foreground">
                    {getApiErrorMessage(error)}
                  </p>
                </div>
                <Button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isFetching}
                >
                  Try again
                </Button>
              </div>
            ) : isFinancialsMockup ? (
              <FinancialsReportMockup />
            ) : isParetoMockup ? (
              <ParetoCurveMockup />
            ) : isAttributesMockup ? (
              <AttributesReportMockup />
            ) : isEventProductFlowMockup ? (
              <EventProductFlowMockup />
            ) : isCostShiftGridMockup ? (
              <CostShiftGridMockup onDrillCell={handleCostShiftDrill} />
            ) : isPoLevelDetailMockup ? (
              <POLevelDetailViewMockup
                initialFilters={drillContext?.filters}
                isDrillMode={!!drillContext}
                onBack={handleDrillBack}
              />
            ) : isStoreCubeCartonMockup ? (
              <StoreCubeCartonUpdateMockup />
            ) : isInboundFlowReportMockup ? (
              <InboundFlowReportMockup />
            ) : isPogOnTimeProjectionMockup ? (
              <POGOnTimeProjectionMockup />
            ) : isSeasonEventDispositionMockup ? (
              <SeasonEventDispositionMockup />
            ) : embedConfig ? (
              <PowerBiReportEmbed
                embedConfig={embedConfig}
                reportType={selectedDashboard}
                pageName={selectedReport?.pageId}
                loadingLabel={`${selectedDashboard} dashboard`}
                onRefresh={handleRefresh}
                isFetching={isFetching}
              />
            ) : null}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
